import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, ButtonGroup, Col, Container, Input, InputGroup, InputGroupText, Row, Table } from "reactstrap";
import { useFacebookFormCore } from "../../cores/facebookForm";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import FormQuestions from "../component/forms/facebook/modal/FormQuestions";
import LeadCard from "../component/forms/facebook/LeadCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { FACEBOOK_FORM_LEADS_LOADING } from "../../constants/loading.constant";
import { useIntl } from "react-intl";
import { CONTACT_EXPAND_CREATION_FORM, FACEBOOK_VIEW_FORM } from "../../constants/modal.constant";
import { useGlobalCore } from "../../cores/globals";
import { NoData } from "../component/NoData";
import "./FacebookForms.scss";
import { contactApiService } from "../../cores/contacts/services/api";
import { ContactSource } from "../../cores/contacts/enum/ContactSource.enum";
import { useContactCore } from "../../cores/contacts";
import { useBoardCore } from "../../cores/boards";
import { CONTACT_EXPAND_EDIT_FORM } from "../../constants/modal.constant";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import EditContactForm from "../component/contacts/modal/EditContactForm";
import { FormLead } from "../../cores/facebookForm/interfaces/api/get-facebook-form";
import CreateContactForm from "../component/contacts/modal/CreateContactForm";

type LeadViewMode = "grid" | "cards" | "table";

export default function ViewFacebookForm() {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const { isDarkMode, turnOffModal, turnOnModal, getModalVisibility, isModal } = useGlobalCore();
  const { company, user, socialSettings, companyRole, isAdmin, isReseller } = useMeCore();
  const { facebookForm, getFacebookForm } = useFacebookFormCore();
  const { goToFacebookForms, goToSocial } = useRouterCore();
  const { postContact, getContact, getContactTags, getCustomFields } = useContactCore();
  const { getBusinessBoards } = useBoardCore();
  const [viewMode, setViewMode] = useState<LeadViewMode>("grid");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");

  useEffect(() => {
    if (
      !socialSettings?.facebook || (
        !companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller
      )
    ) goToSocial();
  }, [goToSocial, socialSettings, companyRole, isAdmin, isReseller]);

  const toggleSideModal = useCallback(() => {
    turnOnModal(FACEBOOK_VIEW_FORM, true);
  }, [turnOnModal]);

  useEffect(() => {
    getFacebookForm(company?.id!, id);
  }, [getFacebookForm, company, id]);

  const leads = useMemo(() => {
    if (!facebookForm?.leads) return [];
    const q = search.trim().toLowerCase();
    const withDate = facebookForm.leads.map((lead) => ({
      ...lead,
      createdAtMs: Date.parse(lead.createdTime || "") || 0,
    }));

    let next = withDate.filter((lead) => {
      if (!q) return true;
      const blob = `${lead.id} ${lead.createdTime} ${(lead.data || []).join(" ")}`.toLowerCase();
      return blob.includes(q);
    });

    next = [...next].sort((a, b) =>
      sortBy === "newest" ? b.createdAtMs - a.createdAtMs : a.createdAtMs - b.createdAtMs
    );
    return next;
  }, [facebookForm?.leads, search, sortBy]);

  const leadStats = useMemo(() => {
    const total = facebookForm?.leads?.length || 0;
    const questions = facebookForm?.form?.questions?.length || 0;
    return { total, questions };
  }, [facebookForm?.leads?.length, facebookForm?.form?.questions?.length]);

  const extractLeadData = useCallback(
    (lead: FormLead) => {
      const questions = facebookForm?.form?.questions || [];
      const answers = lead.data || [];
      const pairs = questions.map((q, i) => ({ q: (q || "").toLowerCase(), a: String(answers[i] || "").trim() }));

      const pick = (keys: string[]) =>
        pairs.find((p) => keys.some((k) => p.q.includes(k)))?.a || "";

      const name =
        pick(["full name", "first name", "name", "nombre"]) ||
        answers[0] ||
        `Lead Facebook ${String(lead.id).slice(-6)}`;
      const email = pick(["email", "correo", "e-mail"]);
      const phone = pick(["phone", "tel", "cel", "whatsapp", "movil", "mobile"]);

      return { name: String(name).trim(), email: String(email).trim(), phone: String(phone).trim() };
    },
    [facebookForm?.form?.questions]
  );

  const getLeadPrimaryLabel = useCallback(
    (lead: FormLead) => {
      const { name, email, phone } = extractLeadData(lead);
      return {
        title: name || "Lead sin nombre",
        subtitle: email || phone || "Sin email ni teléfono",
      };
    },
    [extractLeadData]
  );

  const openContactEditor = useCallback(
    async (contactId: string) => {
      if (!company?.id) return;
      getContactTags(company.id);
      getCustomFields(company.id);
      getContact(company.id, contactId);
      getBusinessBoards(company.id);
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm onDelete={() => {}} onUpdate={() => {}} />,
        Position.LEFT
      );
    },
    [company?.id, getBusinessBoards, getContact, getContactTags, getCustomFields, turnOnModal]
  );

  const onOpenOrCreateContact = useCallback(
    async (lead: FormLead) => {
      if (!company?.id) return;
      const { name, email, phone } = extractLeadData(lead);
      const fallbackToCreateModal = () => {
        turnOnModal(
          CONTACT_EXPAND_CREATION_FORM,
          <CreateContactForm
            source={ContactSource.FACEBOOK_FORM}
            preContactData={{
              name: name || `Lead Facebook ${String(lead.id).slice(-6)}`,
              email: email || "",
              reference: `Facebook Form: ${facebookForm?.form?.name || ""}`.trim(),
            }}
          />,
          Position.LEFT
        );
      };

      try {
        let contactId = "";
        if (email) {
          const byEmail = await contactApiService.getContacts(company.id, {
            page: 1,
            perPage: 1,
            email__p: email,
          });
          contactId = byEmail.contacts?.[0]?.id || "";
        }
        if (!contactId && phone) {
          const byPhone = await contactApiService.getContacts(company.id, {
            page: 1,
            perPage: 1,
            tel__p: phone,
          });
          contactId = byPhone.contacts?.[0]?.id || "";
        }

        if (contactId) {
          await openContactEditor(contactId);
          return;
        }

        const created = await postContact(company.id, {
          name: name || `Lead Facebook ${String(lead.id).slice(-6)}`,
          email: email || "",
          source: ContactSource.FACEBOOK_FORM,
          state: 0,
          phoneNumber: phone || "",
          whatsapp: phone || "",
          reference: `Facebook Form: ${facebookForm?.form?.name || ""}`.trim(),
          customFields: {},
          owners: user?.id ? [user.id] : [],
        });

        if (created?.id) {
          await openContactEditor(created.id);
          return;
        }

        // Si no se pudo crear por backend, al menos abrimos modal de creacion precompletado.
        fallbackToCreateModal();
      } catch (error) {
        console.error(error);
        fallbackToCreateModal();
      }
    },
    [
      company?.id,
      extractLeadData,
      facebookForm?.form?.name,
      openContactEditor,
      postContact,
      turnOnModal,
      user?.id,
    ]
  );

  return (
    <Layout>
      <Header2
        title={facebookForm && facebookForm.form?.name!}
        rightChildren={null}
      />
      <Content>
        <UpdateLoadingCard
          loadingId={FACEBOOK_FORM_LEADS_LOADING}
          text={intl.formatMessage({ id: "app.facebookForm.updatingAnswer" })} 
        />
        <Container fluid className={`facebook-forms-ai ${isDarkMode ? "facebook-forms-ai--dark" : ""}`}>
          {facebookForm ? (
            <>
              <div className="facebook-forms-ai__panel mb-3">
                <div className="facebook-forms-ai__panel-glow" />
                <div className="facebook-forms-ai__panel-top">
                  <div className="facebook-forms-ai__panel-head">
                    <h6 className="facebook-forms-ai__panel-title mb-0">Leads del formulario</h6>
                    <p className="facebook-forms-ai__panel-hint mb-0">
                      Gestiona respuestas y crea contactos desde esta barra.
                    </p>
                  </div>
                  <div className="facebook-forms-ai__stats">
                    <span className="facebook-forms-ai__chip">{leadStats.total} leads</span>
                    <span className="facebook-forms-ai__chip">{leadStats.questions} preguntas</span>
                  </div>
                  <div className="facebook-forms-ai__actions">
                    <div className="facebook-forms-ai__actions-main">
                      <Button color="secondary" outline className="facebook-forms-ai__pill-btn" onClick={goToFacebookForms}>
                        <i className="fas fa-arrow-left mr-1" />
                        Volver a formularios
                      </Button>
                      <Button color="secondary" outline className="facebook-forms-ai__pill-btn" onClick={toggleSideModal}>
                        Preguntas del formulario
                      </Button>
                      <Button color="success" className="facebook-forms-ai__pill-btn facebook-forms-ai__pill-btn--cta">
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          href={`https://www.facebook.com/ads/lead_gen/export_csv/?id=${facebookForm.form?.id}&type=form&source_type=graph_api`}
                          className="text-white text-decoration-none"
                        >
                          Exportar CSV
                        </a>
                      </Button>
                    </div>
                    <div className="facebook-forms-ai__view-selector">
                      <span className="facebook-forms-ai__view-label">Vista</span>
                      <ButtonGroup className="facebook-forms-ai__views mb-1">
                        <Button
                          color="secondary"
                          outline={viewMode !== "grid"}
                          className={`lbutton ${viewMode === "grid" ? "is-active" : ""}`}
                          onClick={() => setViewMode("grid")}
                          aria-pressed={viewMode === "grid"}
                        >
                          <i className="fas fa-table-cells mr-1" />
                          Grilla
                        </Button>
                        <Button
                          color="secondary"
                          outline={viewMode !== "cards"}
                          className={viewMode === "cards" ? "is-active" : ""}
                          onClick={() => setViewMode("cards")}
                          aria-pressed={viewMode === "cards"}
                        >
                          <i className="fas fa-id-card mr-1" />
                          Tarjetas
                        </Button>
                        <Button
                          color="secondary"
                          outline={viewMode !== "table"}
                          className={`rbutton ${viewMode === "table" ? "is-active" : ""}`}
                          onClick={() => setViewMode("table")}
                          aria-pressed={viewMode === "table"}
                        >
                          <i className="fas fa-table-list mr-1" />
                          Tabla
                        </Button>
                      </ButtonGroup>
                    </div>
                  </div>
                </div>
                <Row className="g-2">
                  <Col md="8" xs="12">
                    <InputGroup>
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar lead por nombre, mail, teléfono o contenido"
                      />
                    </InputGroup>
                  </Col>
                  <Col md="4" xs="12">
                    <Input type="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as "newest" | "oldest")}>
                      <option value="newest">Más recientes primero</option>
                      <option value="oldest">Más antiguos primero</option>
                    </Input>
                  </Col>
                </Row>
              </div>

              {leads.length === 0 ? (
                <NoData image="generic3" text="No hay leads con los filtros actuales." horizontal />
              ) : viewMode === "table" ? (
                <div className="facebook-forms-ai__table-wrap">
                  <div className="facebook-forms-ai__table-toolbar">
                    <div>
                      <h5 className="facebook-forms-ai__table-heading mb-0">Leads del formulario</h5>
                      <p className="facebook-forms-ai__table-subheading mb-0">
                        Vista detallada para revisar y abrir contactos rápidamente.
                      </p>
                    </div>
                  </div>
                  <Table responsive hover className="facebook-forms-ai__table mb-0">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        {(facebookForm.form?.questions || []).slice(0, 3).map((question, index) => (
                          <th key={`${question}-${index}`}>{question}</th>
                        ))}
                        <th>Fecha</th>
                        <th>Contacto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id}>
                          <td>
                            <div className="facebook-forms-ai__table-lead">
                              <span className="facebook-forms-ai__table-lead-title">
                                {getLeadPrimaryLabel(lead).title}
                              </span>
                              <span className="facebook-forms-ai__table-lead-subtitle">
                                {getLeadPrimaryLabel(lead).subtitle}
                              </span>
                              <code className="facebook-forms-ai__table-id">{lead.id}</code>
                            </div>
                          </td>
                          {(facebookForm.form?.questions || []).slice(0, 3).map((_, index) => (
                            <td key={`${lead.id}-${index}`}>
                              <span className="facebook-forms-ai__table-answer">
                                {lead.data?.[index] || "-"}
                              </span>
                            </td>
                          ))}
                          <td>
                            <span className="facebook-forms-ai__table-date">{lead.createdTime}</span>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              className="facebook-forms-ai__table-action"
                              onClick={() => onOpenOrCreateContact(lead)}
                            >
                              Abrir / crear contacto
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Row>
                  {leads.map((lead) => (
                    <Col
                      md={viewMode === "grid" ? "4" : "12"}
                      sm={viewMode === "grid" ? "6" : "12"}
                      xs="12"
                      key={lead.id}
                      className="mb-3"
                    >
                      <LeadCard
                        facebookFormLead={lead}
                        questions={facebookForm.form?.questions || []}
                        mode={viewMode === "grid" ? "grid" : "list"}
                        onOpenContact={() => onOpenOrCreateContact(lead)}
                      />
                    </Col>
                  ))}
                </Row>
              )}
            </>
          ) : (
            <NoData
              image="generic3"
              text={intl.formatMessage({ id: "app.facebookForm.notForm" })} 
              horizontal
            />
          )}
        </Container>
      </Content>
      <SideModal
        active={isModal(FACEBOOK_VIEW_FORM)}
        visible={getModalVisibility(FACEBOOK_VIEW_FORM)}
        onClose={() => {
          turnOffModal(FACEBOOK_VIEW_FORM);
        }}
      >
        <FormQuestions questions={facebookForm.form?.questions!} />
      </SideModal>
    </Layout>
  );
}
